/**
 * Google Analytics 4 Composable
 * Provides methods for tracking page views and custom events with GA4
 *
 * Session tracking:
 * - Generates unique session ID using crypto.randomUUID()
 * - Stores session ID in sessionStorage (key: 'aura_session_id')
 * - Includes session ID as custom dimension in all events
 */

// Session ID management
let sessionId = null;

/**
 * Get or create session ID
 * @returns {string} Session ID
 */
function getSessionId() {
  if (sessionId) {
    return sessionId;
  }

  // Try to get from sessionStorage
  const storedSessionId = sessionStorage.getItem('aura_session_id');
  if (storedSessionId) {
    sessionId = storedSessionId;
    return sessionId;
  }

  // Generate new session ID
  sessionId = crypto.randomUUID();
  sessionStorage.setItem('aura_session_id', sessionId);
  console.log('[Analytics] New session ID generated:', sessionId);
  return sessionId;
}

/**
 * Initialize Google Analytics 4
 * @param {string} measurementId - GA4 measurement ID (e.g., 'G-XXXXXXXXXX')
 */
export function initAnalytics(measurementId) {
  if (!measurementId) {
    console.warn('[Analytics] No measurement ID provided, analytics disabled');
    return;
  }

  // Check if gtag is already loaded
  if (window.gtag) {
    console.log('[Analytics] gtag already loaded');
    return;
  }

  console.log('[Analytics] Initializing GA4 with measurement ID:', measurementId);

  // Initialize session ID
  getSessionId();

  // Load gtag.js script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);

  // Initialize gtag function
  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  window.gtag = gtag;

  // Configure gtag
  gtag('js', new Date());
  gtag('config', measurementId, {
    send_page_view: false, // We'll manually send page views
    custom_map: {
      dimension1: 'user_session_id'
    }
  });

  console.log('[Analytics] GA4 initialized successfully');
}

/**
 * Track a page view
 * @param {string} path - Page path (e.g., '/dashboard/vue')
 */
export function trackPageView(path) {
  if (!window.gtag) {
    console.warn('[Analytics] gtag not initialized, skipping page view tracking');
    return;
  }

  const sessionId = getSessionId();
  const eventData = {
    page_path: path,
    user_session_id: sessionId
  };

  console.log('[Analytics] Tracking page view:', path, eventData);
  window.gtag('event', 'page_view', eventData);
}

/**
 * Track a custom event
 * @param {string} eventName - Event name (e.g., 'investigation_expand')
 * @param {Object} params - Event parameters
 */
export function trackEvent(eventName, params = {}) {
  if (!window.gtag) {
    console.warn('[Analytics] gtag not initialized, skipping event tracking');
    return;
  }

  const sessionId = getSessionId();
  const eventData = {
    ...params,
    user_session_id: sessionId
  };

  console.log('[Analytics] Tracking event:', eventName, eventData);
  window.gtag('event', eventName, eventData);
}

/**
 * Vue 3 composable for analytics
 * @returns {Object} Analytics methods
 */
export function useAnalytics() {
  return {
    trackPageView,
    trackEvent
  };
}
